import React from 'react';
import {SlugLocale, LocaleEnum, LanguageEnum} from '../types/locale';
import type {OpenGraphImage} from '../types/openGraph';
import {hrefLangXDefault} from '../utils/hrefLangXDefault';
import {localeToOpenGraphLocale} from '../utils/localeToOpenGraphLocale';
import {localeToPath} from '../utils/localeToPath';
import {config} from '../../config';

type ArticleProps = {
  articleModifiedTime: string;
  articlePublishedTime: string;
};

type HeadBaseType = {
  template: 'page' | 'blogpost' | 'generic' | 'signup' | 'loveCustomer';
  title: string;
  description: string;
  openGraphImage: OpenGraphImage;
  openGraphType: 'article' | 'website';
  noIndex: boolean;
  slugLocales: SlugLocale[];
  currentPathname: string;
  currentLocale: LocaleEnum;
};

type BlogPostHeadType = HeadBaseType & {template: 'blogpost'} & ArticleProps;
type PageHeadType = HeadBaseType & {template: 'page' | 'signup' | 'loveCustomer'; canonicalPathname: string; enableVwo: boolean};
type GenericType = HeadBaseType & {template: 'generic'};

export type HeadType = BlogPostHeadType | PageHeadType | GenericType;

export const HtmlHead: React.FC<HeadType> = props => {
  const canonicalPathname = (props as PageHeadType)?.canonicalPathname || props.currentPathname || '/' + localeToPath(props.currentLocale);
  const shouldEnableVwo = (props.template === 'page' || props.template === 'signup') && props.enableVwo;

  return (
    <>
      <html lang={props.currentLocale} />
      {(config.metaNoIndex || (typeof window === 'object' && window.location.host === 'new.ready2order.com') || props.noIndex) && (
        <meta name='robots' content='noindex' />
      )}
      <title>{props.title}</title>
      <meta name='description' content={props.description} />
      <meta name='robots' content='max-snippet:-1, max-image-preview:large, max-video-preview:-1' />
      <link rel='canonical' href={`${config.siteUrl}${canonicalPathname}`} />
      <meta name='msapplication-TileColor' content='#0A2433' />
      <meta name='msapplication-TileImage' content={`${config.assetPrefixUrl}images/icons/ms-icon-144x144.png`} />
      <meta property='og:locale' content={localeToOpenGraphLocale(props.currentLocale)} />
      <meta property='og:type' content={props.openGraphType} />
      <meta property='og:title' content={props.title} />
      <meta property='og:description' content={props.description} />
      <meta property='og:url' content={`${config.siteUrl}${props.currentPathname}`} />
      <meta property='og:site_name' content='ready2order GmbH' />
      <meta property='article:publisher' content='https://www.facebook.com/ready2order/' />
      <meta property='fb:app_id' content='165486450501706' />
      <meta property='og:image' content={props.openGraphImage.url} />
      <meta property='og:image:secure_url' content={props.openGraphImage.url} />
      <meta property='og:image:width' content={props.openGraphImage.width.toString()} />
      <meta property='og:image:height' content={props.openGraphImage.height.toString()} />
      {props.template === 'blogpost' && (
        <>
          <meta property='article:published_time' content={props.articlePublishedTime} />
          <meta property='article:modified_time' content={props.articleModifiedTime} />
        </>
      )}
      {props.slugLocales.map(slugLocale => (
        <link
          rel='alternate'
          key={slugLocale.locale}
          href={`${config.siteUrl}${slugLocale.slug}`}
          hrefLang={slugLocale.locale === LocaleEnum.EN ? LanguageEnum.EN : slugLocale.locale}
        />
      ))}
      {props.slugLocales.length && <link rel='alternate' href={`${config.siteUrl}${hrefLangXDefault(props.slugLocales).slug}`} hrefLang='x-default' />}
      {shouldEnableVwo && (
        <>
          <link key='vwo-smartcode-preconnect' rel='preconnect' href='https://dev.visualwebsiteoptimizer.com' />
          <script
            async
            type='text/javascript'
            key='gatsby-plugin-vwo-smartcode'
            id='vwoCode'
            dangerouslySetInnerHTML={{
              __html: `
                window._vwo_code || (function() {
                var account_id=${config.vwoId},
                version=2.0,
                settings_tolerance=2000,
                hide_element='body',
                hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important',
                f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(){if(!f){f=true;var e=d.getElementById('_vis_opt_path_hides');if(e)e.parentNode.removeChild(e)}},finished:function(){return f},load:function(e){var t=this.getSettings(),n=d.createElement('script'),i=this;if(t){n.textContent=t;d.getElementsByTagName('head')[0].appendChild(n);if(!w.VWO||VWO.caE){stT.removeItem(cK);i.load(e)}}else{n.fetchPriority='high';n.src=e;n.type='text/javascript';n.onerror=function(){_vwo_code.finish()};d.getElementsByTagName('head')[0].appendChild(n)}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){_vwo_code.finish();stT.removeItem(cK)},e);var t=d.currentScript,n=d.createElement('style'),i=this.hide_element(),r=t&&!t.async&&i?i+this.hide_element_style():'',c=d.getElementsByTagName('head')[0];n.setAttribute('id','_vis_opt_path_hides');v&&n.setAttribute('nonce',v.nonce);n.setAttribute('type','text/css');if(n.styleSheet)n.styleSheet.cssText=r;else n.appendChild(d.createTextNode(r));c.appendChild(n);this.load('https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&vn='+version)}};w._vwo_code=code;code.init();})();
              `
            }}
          />
        </>
      )}
    </>
  );
};
